import dayjs from 'dayjs';

import type { CatalogueDesign, ItemVariantAxis } from '../api/masters';
import { filterShortNames, pickByDigits } from '../units/codePick';
import { normalizeReference } from '../units/referenceNumber';
import type { AvailableUnit } from './rail';

/**
 * The booking's garment search on the website, as on the phone (canvas "Booking flow", 2026-10-26):
 * choose a design's short name, then type only the digits on its tag — `BJB`, then `3601` for
 * `BJB-36-01` — and that design's garments narrow, each with its photo and what stands in its way.
 * The digit rule is the phone's own (`units/codePick.ts`); this adds the rail's answer for the dates.
 */

/** A design's short name in the list: its photo, what it is, and how many are free for the dates. */
export interface ShortNameRow {
  itemId: number;
  code: string;
  name: string;
  imageUrl: string | null;
  free: number;
  total: number;
}

const free = (unit: AvailableUnit, claimed: ReadonlySet<number>) =>
  !unit.heldBy && !claimed.has(unit.unitId);

/**
 * Every design on the rail for the dates, once per short name, in code order — narrowed to a code
 * starting with what is typed or a name containing it. Garments already in the booking count as gone.
 */
export function shortNameRows(
  designs: readonly CatalogueDesign[],
  units: readonly AvailableUnit[],
  claimed: ReadonlySet<number>,
  search = '',
): ShortNameRow[] {
  const seen = new Set<string>();
  const rows = designs
    .filter((design) => design.itemCode.trim() !== '')
    .map((design) => {
      const mine = units.filter((unit) => unit.itemId === design.itemId);

      return {
        itemId: design.itemId,
        code: design.itemCode.trim().toUpperCase(),
        name: design.itemName,
        imageUrl: design.imageUrl,
        free: mine.filter((unit) => free(unit, claimed)).length,
        total: mine.length,
      };
    })
    .filter((row) => row.total > 0)
    .filter((row) => (seen.has(row.code) ? false : (seen.add(row.code), true)))
    .sort((a, b) => a.code.localeCompare(b.code));

  return filterShortNames(rows, search);
}

/** The garments of the design with this short name, in code order. */
export function garmentsOf(
  designs: readonly CatalogueDesign[],
  units: readonly AvailableUnit[],
  code: string,
): AvailableUnit[] {
  const wanted = code.trim().toUpperCase();
  const itemIds = new Set(
    designs
      .filter((design) => design.itemCode.trim().toUpperCase() === wanted)
      .map((design) => design.itemId),
  );

  return units
    .filter((unit) => itemIds.has(unit.itemId))
    .sort((a, b) => a.serialCode.localeCompare(b.serialCode));
}

/**
 * What the digits typed still match, and the row Enter takes: the one garment the digits name
 * exactly when nothing else could still be meant, else the first free one left.
 */
export function searchByDigits(
  designs: readonly CatalogueDesign[],
  units: readonly AvailableUnit[],
  code: string,
  typed: string,
  claimed: ReadonlySet<number>,
): { matches: AvailableUnit[]; next: AvailableUnit | null } {
  const { matches, exact } = pickByDigits(garmentsOf(designs, units, code), code, typed);
  const next =
    exact && free(exact, claimed) ? exact : (matches.find((unit) => free(unit, claimed)) ?? null);

  return { matches, next };
}

/**
 * The left box's **RN** choice (owner, 2026-10-28): the shop's own number instead of a design, for
 * a shop that reads its old tags. Not a design code — those hold no `#` — so it never collides with
 * a short name.
 */
export const byReference = '#RN';

/**
 * Garments by the shop's own number, typed as on the tag ("7", "007", "RN-7", "A12"): those whose
 * number starts with it, the exact one first, then in number order; blank lists every numbered
 * garment. The row Enter takes is the exact one when free, else the first free one left.
 */
export function searchByReference(
  units: readonly AvailableUnit[],
  typed: string,
  claimed: ReadonlySet<number>,
): { matches: AvailableUnit[]; next: AvailableUnit | null } {
  const wanted = normalizeReference(typed) ?? '';
  const matches = units
    .filter((unit) => !!unit.referenceNumber && unit.referenceNumber.startsWith(wanted))
    .sort(
      (a, b) =>
        Number(b.referenceNumber === wanted) - Number(a.referenceNumber === wanted) ||
        a.referenceNumber!.localeCompare(b.referenceNumber!, 'en', { numeric: true }),
    );
  const exact = wanted === '' ? undefined : matches.find((unit) => unit.referenceNumber === wanted);
  const next =
    exact && free(exact, claimed) ? exact : (matches.find((unit) => free(unit, claimed)) ?? null);

  return { matches, next };
}
/** How many leading characters of a code's tail the typed digits cover — for the highlight. */
export function typedLength(serialCode: string, code: string, typed: string): number {
  const tail = serialCode.slice(code.length + 1);
  const digits = typed.replace(/\D/g, '');
  let covered = 0;
  let cut = 0;

  while (cut < tail.length && covered < digits.length) {
    if (/\d/.test(tail[cut]!)) {
      covered++;
    }

    cut++;
  }

  return cut;
}

const day = (value: string) => dayjs(value.slice(0, 10)).format('D MMM');

/**
 * A garment's line in the digits list: whether it can be taken, and why not when it cannot — in
 * this booking already (and for whom), held by another customer, booked, or out for cleaning.
 */
export function garmentState(
  unit: AvailableUnit,
  inThisBookingFor: string | null,
): { free: boolean; words: string } {
  if (inThisBookingFor !== null) {
    return { free: false, words: `In the bag for ${inThisBookingFor}` };
  }

  const held = unit.heldBy;

  if (!held) {
    return { free: true, words: 'Free for these days' };
  }

  const span = `${day(held.fromDate)} – ${day(held.untilDate)}`;

  if (held.holder) {
    const who = held.holder.customerName || 'another customer';

    return {
      free: false,
      words: held.tentative
        ? `Held for ${who} · ${span}`
        : `Booked${held.holder.documentNumber ? ` · ${held.holder.documentNumber}` : ''} for ${who} · ${span}`,
    };
  }

  return { free: false, words: `${held.reason || 'Not free'} · until ${day(held.untilDate)}` };
}

/** One boxed chip on a garment's row: "Size 36", a colour with its swatch, "Regular fit". */
export interface AxisChip {
  key: string;
  label: string;
  colourHex: string | null;
}

/**
 * A garment's size and look as chips (owner, 2026-10-28, board A of "RN search results"): the
 * garment-code axes first, named ("Size 36"), then the look's values in attribute order, a colour
 * carrying its swatch. The rail's own answer has only the variant's one-line name, so the axes come
 * from the catalogue beside it; a garment the catalogue does not reach falls back to that name's
 * parts ("Size 36 / Colour Black" → "Size 36", "Colour Black").
 */
export function axisChips(
  axes: readonly ItemVariantAxis[] | undefined,
  variantDisplayName: string,
): AxisChip[] {
  if (!axes || axes.length === 0) {
    return variantDisplayName
      .split('/')
      .map((part) => part.trim())
      .filter((part) => part !== '')
      .map((part) => ({ key: part, label: part, colourHex: null }));
  }

  return [...axes]
    .sort(
      (a, b) => Number(b.inGarmentCode) - Number(a.inGarmentCode) || a.attributeId - b.attributeId,
    )
    .map((axis) => ({
      key: `${axis.attributeId}:${axis.valueId}`,
      label: axis.inGarmentCode ? `${axis.attributeName} ${axis.valueName}` : axis.valueName,
      colourHex: axis.colourHex,
    }));
}

/** Each garment's axes by its id, read off the catalogue — what `axisChips` is given. */
export function axesByUnit(
  designs: readonly CatalogueDesign[],
): ReadonlyMap<number, readonly ItemVariantAxis[]> {
  return new Map(
    designs.flatMap((design) =>
      (design.units ?? []).map((unit) => [unit.unitId, unit.axes] as const),
    ),
  );
}
